import React, { useEffect, useState, useRef } from 'react';
import { Liquid } from 'liquidjs';
import './TemplateEngine.css';
import Grid from '@mui/material/Grid';
import sharedStyles from '../Masters/Masters.module.css';
import Breadcrumb from '../../components/BreadCrumb/breadCrumb';
import Strings from '../../constants/stringConstant';
import { Box } from '@mui/system';
import DropdownTextComp from '../../components/Dropdown/dropDownTextComp';
import TabSection from '../LoanApplicationsPage/Common/Tab/Tab';
import ButtonComp from '../../components/Button/buttonComp';
import { toast } from 'react-toastify';
import {
  fetchDocumentTemplate,
  fetchTemplateVariables,
  saveDocumentTemplate,
} from '../../api/request/masterFetchApi/masterFetchApi';
import makeApiCall from '../../api/makeApiCall';
import { productListApi } from '../../api/request/configApi/configApi';
import { useSelector } from 'react-redux';
export default function TemplateEngine() {
  const [selectedVariable, setSelectedVariable] = useState('');
  const paths = [{ name: 'Template Engine', link: '/masters/template-engine' }];
  const [activeTab, setActiveTab] = useState('HTML Editor');
  const [productListing, setProductListing] = useState('');
  const [templateListing, setTemplateListing] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [liquidTemplateContent, setLiquidTemplateContent] = useState('');
  const [variableListing, setVariableListing] = useState([]);
  const [htmlContent, setHtmlContent] = useState('');
  const [cssContent, setCssContent] = useState('');
  const [productListVal, setProductListVal] = useState([]);
  const { roleBased } = useSelector(state => state.rbacValue);
  const isEdit =
    roleBased?.template_engine?.sections?.template_engine !== 'edit';
  const htmlEditorRef = useRef(null);
  useEffect(() => {
    fetchProductList();
  }, []);

  function wrapCurlyBracesWithRaw(template) {
    return template.replace(/(\{\{[^}]+\}\})/g, '{% raw %}$1{% endraw %}');
  }

  useEffect(() => {
    const renderLiquid = async () => {
      if (!selectedTemplate || !liquidTemplateContent) {
        setHtmlContent('');
        setCssContent('');
        return;
      }
      try {
        const engine = new Liquid();
        const variables = {
          authorizationLetterApplicant: {
            kotaklogo: 'https://www.kotaklife.com/assets/images/logo.svg',
            iifllogo: 'https://www.iifllogo.com/assets/images/logo.svg',
            iiflSamastalogo:
              'https://www.iiflsamastalogo.com/assets/images/logo.svg',
          },
        };
        const processedTemplate = wrapCurlyBracesWithRaw(liquidTemplateContent);
        const rendered = await engine.parseAndRender(
          processedTemplate,
          variables,
        );
        const styleMatch = rendered.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
        const css = styleMatch ? styleMatch[1] : '';
        const bodyMatch = rendered.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        const html = bodyMatch ? bodyMatch[1] : rendered;
        setHtmlContent(html);
        setCssContent(css);
      } catch (err) {
        setHtmlContent(
          '<div style="color:red">Failed to load/render .liquid template.</div>',
        );
        setCssContent('');
      }
    };
    renderLiquid();
  }, [liquidTemplateContent, selectedTemplate]);

  const fetchProductList = async () => {
    try {
      const response = await makeApiCall(productListApi());
      if (response?.data?.status === Strings.success) {
        const products = response?.data?.data?.map(product => ({
          productId: product?.productId,
          productCategory: product?.productCategory,
        }));
        setProductListVal(products);
      } else {
        setProductListVal([]);
      }
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const fetchDocumentTemplateApi = async productId => {
    try {
      const response = await makeApiCall(fetchDocumentTemplate({ productId }));
      if (response?.data?.status === Strings?.success) {
        setTemplateListing(response?.data?.data ?? []);
      } else {
        setTemplateListing([]);
      }
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const fetchTemplateVariableApi = async templateId => {
    try {
      const response = await makeApiCall(fetchTemplateVariables({ templateId }));
      if (response?.data?.status === Strings?.success) {
        const data = response?.data?.data ?? [];
        const normalized = data.map(item => ({
          variable: item.variable || '',
          placeholder: item.placeholder || '',
        }));
        setVariableListing(normalized);
      } else {
        setVariableListing([]);
      }
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const saveTemplateApi = async (
    templateId,
    template,
    templateName,
    referencePath,
  ) => {
    try {
      const response = await makeApiCall(
        saveDocumentTemplate({
          templateId,
          template,
          templateName,
          referencePath,
        }),
      );
      if (response?.data?.status === Strings?.success) {
        toast.success('Template saved successfully', { position: 'top-right' });
        if (selectedTemplate?.productId) {
          fetchDocumentTemplateApi(selectedTemplate.productId);
        }
      } else {
        console.log('Failed to save template');
      }
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const tabs = [
    {
      tabName: 'HTML Editor',
      component: (
        <div>
          <textarea
            ref={htmlEditorRef}
            name="htmlContent"
            value={htmlContent}
            onChange={event => setHtmlContent(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Backspace' || event.key === 'Delete') {
                const el = htmlEditorRef.current;
                const { selectionStart, selectionEnd } = el;
                const value = htmlContent;
                if (selectionStart === selectionEnd) {
                  const before = value.lastIndexOf('{{', selectionStart);
                  const after = value.indexOf('}}', selectionStart);
                  if (
                    before !== -1 &&
                    after !== -1 &&
                    selectionStart > before &&
                    selectionStart <= after + 2
                  ) {
                    const newValue =
                      value.slice(0, before) + value.slice(after + 2);
                    setHtmlContent(newValue);
                    setTimeout(() => el.setSelectionRange(before, before), 0);
                    event.preventDefault();
                  }
                }
              }
            }}
            rows={22}
            placeholder="Write HTML here..."
            style={{
              width: '100%',
              fontFamily: 'Manrope',
              fontSize: 16,
              padding: 8,
              borderRadius: 4,
              border: `2px solid ${'#f37021'}`,
              resize: 'vertical',
              minHeight: 300,
              whiteSpace: 'pre',
              tabSize: 2,
              outline: 'none',
            }}
          />
        </div>
      ),
    },
    {
      tabName: 'CSS Editor',
      component: (
        <div>
          <textarea
            name="cssContent"
            value={cssContent}
            onChange={event => setCssContent(event.target.value)}
            rows={22}
            placeholder="Write CSS here..."
            style={{
              width: '100%',
              fontFamily: 'Manrope',
              fontSize: 16,
              padding: 8,
              borderRadius: 4,
              border: `2px solid ${'#f37021'}`,
              resize: 'vertical',
              minHeight: 300,
              whiteSpace: 'pre',
              tabSize: 2,
              outline: 'none',
            }}
          />
        </div>
      ),
    },
    {
      tabName: 'Preview',
      component: (
        <iframe
          title="Template Preview"
          style={{
            width: '100%',
            height: '600px',
            background: '#fff',
            border: '1px solid #e4e4e4',
          }}
          srcDoc={`<!DOCTYPE html>
<html>
  <head>
    <style>${cssContent}</style>
  </head>
  <body>
    ${htmlContent}
  </body>
</html>`}
        />
      ),
    },
  ];
  const handleTabChange = tabName => {
    setActiveTab(tabName);
    if (tabName !== 'HTML Editor') {
      setSelectedVariable('');
    }
  };

  return (
    <Box className={sharedStyles.main_container}>
      <Breadcrumb paths={paths} />
      <Box className={sharedStyles.secondary_container}>
        <Box className={'text-medium-16px light-purple-color'}>
          {Strings.templateEngine.templateEngineDetail}
        </Box>
        <Box className="main-content">
          <Grid container spacing={'16px'}>
            <Grid item xs={12} md={6}>
              <DropdownTextComp
                label="Product"
                name="product"
                selectedValue={productListing}
                handleChange={event => {
                  const newProductId = event.target.value;
                  setProductListing(newProductId);
                  setSelectedTemplate(null);
                  setLiquidTemplateContent('');
                  fetchDocumentTemplateApi(newProductId);
                }}
                options={productListVal.map(product => ({
                  value: product.productId,
                  label: product.productCategory,
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DropdownTextComp
                label="Template"
                name="template"
                disabled={!productListing}
                selectedValue={selectedTemplate?.templateId || ''}
                handleChange={async event => {
                  const selected = templateListing.find(
                    id => id.templateId === event.target.value,
                  );
                  setSelectedTemplate(selected);
                  if (selected?.templateId)
                    fetchTemplateVariableApi(selected.templateId);
                  if (selected?.template) {
                    try {
                      const resp = await fetch(selected.template);
                      const text = await resp.text();
                      setLiquidTemplateContent(text);
                    } catch {
                      toast.error('Failed to fetch template file', {
                        position: 'top-right',
                      });
                      setLiquidTemplateContent('');
                    }
                  } else {
                    setLiquidTemplateContent('');
                  }
                }}
                options={templateListing.map(template => ({
                  value: template.templateId,
                  label: template.templateName,
                }))}
                required
              />
            </Grid>
          </Grid>
          {selectedTemplate && (
            <Box className="flex-row-gap">
              <Box className="flex-2-minwidth0">
                <Box className="tab-header">
                  <TabSection
                    loanType="Display"
                    tabValue={activeTab}
                    handleChange={handleTabChange}
                    tabs={tabs}
                  />
                </Box>
                <Box className={sharedStyles.inner_container}>
                  {tabs.find(tab => tab.tabName === activeTab)?.component}
                </Box>
              </Box>
              <Box className="variables-panel">
                <Box className="variables-panel-inner">
                  <Box className={'text-medium-16px light-purple-color'}>
                    {Strings.templateEngine.addVariable}
                  </Box>
                  <DropdownTextComp
                    label="Variable"
                    name="variable"
                    selectedValue={selectedVariable}
                    handleChange={event => {
                      const placeholderValue = event.target.value;
                      setSelectedVariable(placeholderValue);
                      if (htmlEditorRef.current) {
                        const el = htmlEditorRef.current;
                        el.focus();
                        const { selectionStart, selectionEnd } = el;
                        const newValue =
                          htmlContent.slice(0, selectionStart) +
                          `{{${placeholderValue}}}` +
                          htmlContent.slice(selectionEnd);
                        setHtmlContent(newValue);
                        const pos =
                          selectionStart + `{{${placeholderValue}}}`.length;
                        setTimeout(() => el.setSelectionRange(pos, pos), 0);
                      }
                    }}
                    options={
                      Array.isArray(variableListing)
                        ? variableListing.map(id => ({
                          value: id.placeholder,
                          label: id.variable,
                        }))
                        : []
                    }
                    disabled={activeTab !== 'HTML Editor'}
                    required
                  />
                  <div className="bottom-button">
                    <ButtonComp
                      className="text-bold-16px"
                      value="Apply"
                      gradient="linear-gradient(168deg, #F37021 -13.5%, #FDBA12 119.9%)"
                      height="48px"
                      width="100%"
                      padding="14px 24px"
                      textcolor="#fff"
                      borderRadius="8px"
                      disabled={isEdit}
                      onClick={() => {
                        if (!selectedTemplate) {
                          toast.error('No template selected', {
                            position: 'top-right',
                          });
                          return;
                        }
                        const { templateId, templateName } = selectedTemplate;
                        if (!htmlContent && !cssContent) {
                          toast.error('No template content to save', {
                            position: 'top-right',
                          });
                          return;
                        }
                        const combinedHtml = `<!DOCTYPE html>\n<html>\n<head>\n  <style>${cssContent}</style>\n</head>\n<body>\n  ${htmlContent}\n</body>\n</html>`;
                        const base64Template = btoa(
                          unescape(encodeURIComponent(combinedHtml)),
                        );
                        const referencePath =
                          selectedTemplate?.referencePath || '';
                        saveTemplateApi(
                          templateId,
                          base64Template,
                          templateName,
                          referencePath,
                        );
                      }}
                    />
                  </div>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
